/**
 * Locale definitions for regional AI visibility analysis.
 *
 * When a locale is selected, a system message prefix is prepended
 * to LLM queries to simulate regional context.
 */

export interface RadarLocale {
  code: string
  name: string
  flag: string
  systemPrefix: string
}

export const LOCALES: RadarLocale[] = [
  {
    code: 'global',
    name: 'Global (Default)',
    flag: '🌐',
    systemPrefix: '',
  },
  {
    code: 'us',
    name: 'United States',
    flag: '🇺🇸',
    systemPrefix:
      'Respond as if you are assisting a user located in the United States. Prioritize US-based companies, US pricing, and the US market when relevant.',
  },
  {
    code: 'uk',
    name: 'United Kingdom',
    flag: '🇬🇧',
    systemPrefix:
      'Respond as if you are assisting a user located in the United Kingdom. Prioritize UK-based companies, GBP pricing, and the UK market when relevant.',
  },
  {
    code: 'eu',
    name: 'European Union',
    flag: '🇪🇺',
    systemPrefix:
      'Respond as if you are assisting a user located in the European Union. Prioritize EU-based companies, EUR pricing, and the European market when relevant. Consider GDPR and EU regulations.',
  },
  {
    code: 'apac',
    name: 'Asia Pacific',
    flag: '🌏',
    systemPrefix:
      'Respond as if you are assisting a user located in Asia Pacific (Southeast Asia, Australia, Japan, South Korea). Prioritize regional companies and consider local market conditions.',
  },
  {
    code: 'latam',
    name: 'Latin America',
    flag: '🌎',
    systemPrefix:
      'Respond as if you are assisting a user located in Latin America. Prioritize regional companies and consider local market conditions, language preferences, and pricing in local currencies.',
  },
]

export function getLocale(code: string): RadarLocale {
  return LOCALES.find(l => l.code === code) || LOCALES[0]
}

/**
 * Build query options with locale system message.
 * Returns undefined systemMessage for global locale (no regional bias).
 */
export function getLocaleQueryOptions(localeCode?: string): {
  systemMessage?: string
} {
  if (!localeCode || localeCode === 'global') return {}
  const locale = getLocale(localeCode)
  return locale.systemPrefix ? { systemMessage: locale.systemPrefix } : {}
}
